import {
  formatPrice,
  formatTimeFromMinutes,
  HOUR,
  MINUTE,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
  type HourlyOrderInput,
  type Scalars,
} from '../../../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../../../context/AuthContext';
import { getFirstErrorMessage } from '../../../../utils/apollo';
import {
  BUY_COACHING_REGISTER_OPTIONS,
  setLocalCheckoutOrderInputs,
  setLocalCheckoutSessionInputs,
} from '../../../../utils/checkout';
import { wasMutationSuccessful } from '../../../../utils/mutations';
import { DEFAULT_HOURLY_ORDER_MINUTES } from '../../../../utils/order';
import { getUrlString } from '../../../../utils/url';
import LoadingIndicator from '../../../common/LoadingIndicator';
import { type InputOption, Radio } from '../../../forms/Inputs';
import HourMinuteInput from '../../../inputs/HourMinuteInput';
import { BookCoachingTimeForm } from '../BookHourlyCoachingModal/BookCoachingTimeForm';
import { type SessionOption } from '../ScheduleModal';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import { sessionLengthToLabelValue } from './ScheduleModalSidebar';
import {
  type AddAdditionalTimeCoachFragment,
  type AddAdditionalTimeCreateOrderMutationVariables,
  useAddAdditionalTimeAdditionalTimeNeededToScheduleQuery,
  useAddAdditionalTimeCreateOrderMutation,
  useAddAdditionalTimeInitialLoadQuery,
} from './__generated-gql-types__/AddAdditionalTime.generated';
import { type BaseScheduleModalLayoutOrderFragment } from './__generated-gql-types__/BaseScheduleModalLayout.generated';
import { type CoachCalendarCoachingSessionFragment } from './__generated-gql-types__/CoachCalendar.generated';

interface AddAdditionalTimeProps
  extends BaseScheduleSubmissionModalLayoutProps {
  categoryUrn: Possible<Scalars['CategoryUrn']['output']>;
  coach: AddAdditionalTimeCoachFragment;
  sessionLength: number;
  sessionType: SessionOption;
  sessionPrice: number;
  hasUnlimitedBookingPower: boolean;
  orderToAssignTime?: number;
  orderToAssignId?: string;
  coachingSession: Possible<CoachCalendarCoachingSessionFragment>;
  order?: Possible<BaseScheduleModalLayoutOrderFragment>;
}

export const AddAdditionalTime: FC<AddAdditionalTimeProps> = ({
  categoryUrn,
  coach,
  coachingSession,
  sessionType,
  sessionPrice,
  selectedTime,
  sessionLength,
  hasUnlimitedBookingPower,
  orderToAssignTime,
  orderToAssignId,
  order,
  recurringCadence,
  ...baseScheduleSubmissionModalLayoutProps
}) => {
  const router = useRouter();
  const { isNewSubscriptionExperience } = useAuth();
  const isNewExperienece = isNewSubscriptionExperience || !!coach.newExperience;

  const { data: initialLoadData, loading: initialDataLoading } =
    useAddAdditionalTimeInitialLoadQuery({
      variables: { coachUrn: coach.id },
    });
  const [orderToUse, setOrderToUse] = useState<Possible<string>>(order?.id);

  const [submitError, setSubmitError] = useState<string | null>(null);
  enum AddAdditionalTimeOptions {
    JUST_THIS_SESSION = 'JUST_THIS_SESSION',
    SELECTED_SESSION_PLUS_ADDITIONAL_HOURS = 'SELECTED_SESSION_PLUS_ADDITIONAL_HOURS',
  }
  const [addAdditionalTimeAction] = useAddAdditionalTimeCreateOrderMutation();
  const {
    register,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<
    AddAdditionalTimeCreateOrderMutationVariables & {
      amount: AddAdditionalTimeOptions;
    }
  >();
  const { data: additionalTimeNeededData } =
    useAddAdditionalTimeAdditionalTimeNeededToScheduleQuery({
      variables: {
        coachUrn: coach.id,
        endAt: (selectedTime ?? 0) + sessionLength * MINUTE,
        startAt: selectedTime ?? 0,
        excludeSessions: coachingSession?.id ? [coachingSession.id] : [],
      },
      skip: !selectedTime || !isNewExperienece,
    });

  const subscriptionSkuUnitPrice = initialLoadData?.activeCustomerSubscriptions
    ?.filter((s) => s.useSkuUnitPrice)
    ?.at(0)?.sku.unitPrice;

  const hourlyRate = coach.hourlyRate;

  const [totalPrice, setTotalPrice] = useState<number>(
    (sessionLength / 60) * (hourlyRate ?? 0),
  );

  useEffect(() => {
    register(
      'hourlyOrder.timeInMinute',
      BUY_COACHING_REGISTER_OPTIONS.TIME_IN_MINUTE,
    );
  }, [register]);

  const { availableTime = 0 } = coach.coachingTimeSummary ?? {};

  const availableCoachingTimeInMinutes = useMemo<number>(() => {
    if (hasUnlimitedBookingPower) return Infinity;
    if (isNewExperienece) {
      if (additionalTimeNeededData?.additionalTimeNeededToSchedule) {
        return (
          sessionLength -
          additionalTimeNeededData.additionalTimeNeededToSchedule
        );
      }
      return availableTime;
    }

    const existingBalanceFromSession = coachingSession?.durationInMinute ?? 0;
    return availableTime + existingBalanceFromSession;
  }, [
    hasUnlimitedBookingPower,
    isNewExperienece,
    availableTime,
    coachingSession?.durationInMinute,
    additionalTimeNeededData?.additionalTimeNeededToSchedule,
    sessionLength,
  ]);

  const ledger: string[] = [];
  if (!hasUnlimitedBookingPower) {
    if (availableCoachingTimeInMinutes > 0) {
      ledger.push(
        `${availableCoachingTimeInMinutes} minutes from your balance`,
      );
    }
    ledger.push(
      `${sessionLength - availableCoachingTimeInMinutes} minutes left to purchase`,
    );
  }

  const options: InputOption[] = [
    {
      label: (
        <div className="ml-3 flex flex-col text-left">
          <span>
            Just this session
            {availableCoachingTimeInMinutes > 0
              ? ` (${sessionLengthToLabelValue[sessionLength]})`
              : null}
          </span>
          {ledger.map((item, index) => (
            <span key={index} className="text-leland-gray-light">
              {item}
            </span>
          ))}
        </div>
      ),
      value: AddAdditionalTimeOptions.JUST_THIS_SESSION,
    },
    {
      label: (
        <div className="ml-3 flex flex-col text-left">
          <span>Selected session + additional hours</span>
          <span className="text-leland-gray-light">
            Purchase some extra time to schedule future sessions
          </span>
        </div>
      ),
      value: AddAdditionalTimeOptions.SELECTED_SESSION_PLUS_ADDITIONAL_HOURS,
    },
  ];

  const watchedAmount = watch('amount');
  const watchedTimeInMinutes = watch('hourlyOrder.timeInMinute');

  const totalAmount = useMemo(() => {
    if (
      watchedAmount ===
      AddAdditionalTimeOptions.SELECTED_SESSION_PLUS_ADDITIONAL_HOURS
    ) {
      return (
        sessionLength -
        availableCoachingTimeInMinutes +
        (isNaN(watchedTimeInMinutes ?? 0) ? 0 : (watchedTimeInMinutes ?? 0))
      );
    }
    return sessionLength;
  }, [
    availableCoachingTimeInMinutes,
    sessionLength,
    watchedAmount,
    watchedTimeInMinutes,
    AddAdditionalTimeOptions.SELECTED_SESSION_PLUS_ADDITIONAL_HOURS,
  ]);

  const totalMinutes = useMemo(
    () =>
      watchedAmount === AddAdditionalTimeOptions.JUST_THIS_SESSION
        ? sessionLength - availableCoachingTimeInMinutes
        : totalAmount,
    [
      availableCoachingTimeInMinutes,
      sessionLength,
      totalAmount,
      watchedAmount,
      AddAdditionalTimeOptions.JUST_THIS_SESSION,
    ],
  );

  const totalHourMinuteHumanReadable = useMemo(
    () => formatTimeFromMinutes(totalMinutes),
    [totalMinutes],
  );

  const setExtraTime = useCallback(
    async (timeInMinutes: number) => {
      setValue('hourlyOrder.timeInMinute', timeInMinutes);

      const res = await addAdditionalTimeAction({
        variables: {
          coachUrn: coach.id,
          hourlyOrder: {
            timeInMinute:
              sessionLength - availableCoachingTimeInMinutes + timeInMinutes,
          },
          categoryUrn: categoryUrn ?? null,
        },
      });

      if (wasMutationSuccessful(res)) {
        if (res.data?.createOrder.id) {
          setOrderToUse(res.data.createOrder.id);
        }
        if (res.data?.createOrder?.finalAmount) {
          setTotalPrice(res.data.createOrder.finalAmount);
        }
      } else {
        const errorMessage = getFirstErrorMessage(res.errors);
        setSubmitError(errorMessage ?? null);
        throw new Error(errorMessage);
      }
    },
    [
      addAdditionalTimeAction,
      availableCoachingTimeInMinutes,
      categoryUrn,
      coach.id,
      sessionLength,
      setValue,
    ],
  );

  const onSubmitWrapper: SubmitHandler<
    AddAdditionalTimeCreateOrderMutationVariables
  > = async () => {
    if (isNewSubscriptionExperience) {
      const hourlyInput: Optional<HourlyOrderInput> = {
        timeInMinute: totalMinutes,
      };

      setLocalCheckoutOrderInputs({
        coachUrn: coach.id,
        hourlyOrder: hourlyInput,
        categoryId: categoryUrn ?? '',
        orderId: orderToUse,
      });

      setLocalCheckoutSessionInputs({
        selectedTime,
        sessionLength,
        sessionCoachId: coach.id,
        ...(coachingSession ? { coachingSessionUrn: coachingSession?.id } : {}),
        recurringCadence,
      });

      await router.push({
        pathname: '/checkout',
      });
    } else {
      setLocalCheckoutSessionInputs({
        selectedTime,
        sessionLength,
        sessionCoachId: coach.id,
        ...(coachingSession ? { coachingSessionUrn: coachingSession?.id } : {}),
        recurringCadence,
      });
      await router.push(
        getUrlString(`/checkout`, {
          order: orderToUse,
        }),
      );
    }
  };

  // If the user could use subscription unit price to purchase, use the free-text way with HourMinuteInput
  const useBookCoachingTimeForm = isNewExperienece && !subscriptionSkuUnitPrice;

  useEffect(() => {
    if (!useBookCoachingTimeForm) {
      void setExtraTime(0);
    }
  }, [setExtraTime, useBookCoachingTimeForm]);

  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        type: 'submit',
        form: useBookCoachingTimeForm
          ? 'book-coaching-time-form'
          : 'add_additional_time_form',
        disabled: isSubmitting || (!isNewExperienece && !watchedAmount),
      }}
      coach={coach}
      selectedTime={selectedTime}
      sessionLength={sessionLength}
      sessionType={sessionType}
      {...baseScheduleSubmissionModalLayoutProps}
      preventScroll
      recurringCadence={recurringCadence}
      sidebarOptions={{
        title: 'Add time to your balance.',
        subtitle: `To continue with this session, purchase coaching time from ${coach.user.firstName}.`,
      }}
    >
      {initialDataLoading ? (
        <LoadingIndicator className="p-8" iconClassName="size-20" />
      ) : useBookCoachingTimeForm ? (
        <BookCoachingTimeForm
          categoryUrn={categoryUrn}
          coach={coach}
          defaultValues={{
            coachUrn: coach.id,
            hourlyOrder: {
              timeInMinute: DEFAULT_HOURLY_ORDER_MINUTES,
            },
            numberOfCharges: 1,
            categoryUrn,
          }}
          services={
            coach.categoryInformationList?.find(
              (c) => c.category.id === categoryUrn,
            )?.coachingFocusList
          }
          sessionToSchedule={
            selectedTime
              ? {
                  selectedTime,
                  sessionLength,
                  sessionCoachId: coach.id,
                  coachingSessionUrn: coachingSession?.id,
                  recurringCadence,
                }
              : null
          }
          justThisSessionTime={
            sessionLength > availableCoachingTimeInMinutes
              ? sessionLength -
                availableCoachingTimeInMinutes -
                (orderToAssignTime ?? 0)
              : undefined
          }
          justThisSessionPrice={sessionPrice}
          orderToAssignId={orderToAssignId ?? undefined}
        />
      ) : (
        <form
          id="add_additional_time_form"
          className="mx-auto flex w-full max-w-lg grow flex-col items-center overflow-scroll overscroll-contain p-6 text-center md:pb-40"
          onSubmit={handleSubmit(onSubmitWrapper)}
        >
          <p className="font-medium text-leland-red">{submitError}</p>
          <h3 className="text-2xl font-medium text-leland-gray-dark">
            Add additional time?
          </h3>
          <p className="mt-2 text-lg text-leland-gray-light">
            Often times, Leland users will purchase some extra time to schedule
            more sessions later.
          </p>
          <div className="mt-6 w-full">
            <Radio
              fullWidth
              labelIsHidden
              labelClassName="mt-3 rounded border border-leland-gray-stroke p-5 w-full"
              label="Select an option"
              options={options}
              register={register('amount')}
            />
          </div>
          {watchedAmount ===
          AddAdditionalTimeOptions.SELECTED_SESSION_PLUS_ADDITIONAL_HOURS ? (
            <div className="mt-[-4px] w-full rounded-b border border-leland-gray-stroke bg-leland-white p-4">
              <div className="flex w-full justify-between border-b border-b-leland-gray-stroke pb-4 text-leland-gray-light">
                <span className="">Selected session:</span>
                <div className="flex flex-col text-right">
                  {ledger.map((item, index) => (
                    <span key={index} className="text-leland-gray-light">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <p className="mt-3 w-full text-left text-leland-gray-light">
                Additional time:
              </p>
              <HourMinuteInput
                className="w-full"
                valueInMinutes={watchedTimeInMinutes ?? 0}
                max={(100 * HOUR) / MINUTE}
                onChange={setExtraTime}
                error={errors.hourlyOrder?.timeInMinute?.message}
              />
              <div className="mt-3 flex w-full justify-between">
                <span className="text-leland-gray-light">Total</span>
                <span className="text-leland-gray-light">
                  {totalHourMinuteHumanReadable} for {formatPrice(totalPrice)}
                </span>
              </div>
            </div>
          ) : null}
        </form>
      )}
    </BaseScheduleSubmissionModalLayout>
  );
};
